import React from "react";
import LegalPage, { Section, Bullets } from "./LegalPage";

export default function Terms() {
  return (
    <LegalPage
      testId="terms-page"
      eyebrow="Legal"
      title="Terms & Conditions"
      lastUpdated="23 April, 2026"
    >
      <p>
        Welcome to <strong>ResyNote</strong>. By accessing or using our application, you agree to comply
        with and be bound by the following Terms and Conditions. If you do not agree, please do not use
        the service.
      </p>

      <Section n="1" title="Acceptance of Terms">
        <p>By registering or using ResyNote, you confirm that you:</p>
        <Bullets
          items={[
            "Are at least 18 years old or have legal consent",
            "Will use the platform for lawful academic and research purposes only",
          ]}
        />
      </Section>

      <Section n="2" title="Description of Service">
        <p>ResyNote is a research management platform that allows users to:</p>
        <Bullets
          items={[
            "Plan and track experiments",
            "Record observations and results",
            "Generate reports",
            "Collaborate within research groups",
          ]}
        />
        <p>We reserve the right to modify or discontinue features at any time.</p>
      </Section>

      <Section n="3" title="User Accounts">
        <Bullets
          items={[
            "Users must provide accurate information during registration",
            "You are responsible for maintaining account confidentiality",
            "You are responsible for all activity under your account",
          ]}
        />
        <p>ResyNote is not liable for unauthorized access due to user negligence.</p>
      </Section>

      <Section n="4" title="User Roles">
        <p>ResyNote supports:</p>
        <Bullets
          items={[
            "Supervisor Accounts (manage groups, monitor progress, schedule meetings)",
            "Lab Member Accounts (conduct and log experiments)",
          ]}
        />
        <p>Supervisors are responsible for managing their group data and permissions.</p>
      </Section>

      <Section n="5" title="Data Ownership & Responsibility">
        <Bullets
          items={[
            "Users retain ownership of their uploaded data and research content",
            "You are responsible for ensuring data accuracy and backups",
          ]}
        />
        <p>ResyNote is not responsible for data loss due to:</p>
        <Bullets
          items={[
            "User actions",
            "Third-party integrations",
            "System failures beyond reasonable control",
          ]}
        />
      </Section>

      <Section n="6" title="Acceptable Use">
        <p>Users agree NOT to:</p>
        <Bullets
          items={[
            "Upload illegal, harmful, or plagiarized content",
            "Misuse the platform for non-research activities",
            "Attempt to hack, disrupt, or reverse-engineer the system",
          ]}
        />
        <p>Violation may result in account suspension or termination.</p>
      </Section>

      <Section n="7" title="Subscription & Billing">
        <Bullets
          items={[
            "ResyNote offers a 15-day free trial for new users",
          ]}
        />
        <p>After trial:</p>
        <Bullets
          items={["₹199/month subscription", "₹1999/year subscription"]}
        />
        <p>Subscriptions are billed in advance and are subject to change with prior notice.</p>
      </Section>

      <Section n="8" title="Third-Party Integrations">
        <p>
          ResyNote may integrate with services like Google Meet. We are not responsible for:
        </p>
        <Bullets
          items={[
            "Service interruptions",
            "Data handling by third-party platforms",
          ]}
        />
        <p>Users must comply with respective third-party terms.</p>
      </Section>

      <Section n="9" title="Intellectual Property">
        <p>
          All app content, design, and software are owned by ResyNote and protected by applicable laws.
          Users may not copy, reproduce, or distribute without permission.
        </p>
      </Section>

      <Section n="10" title="Limitation of Liability">
        <p>ResyNote shall not be liable for:</p>
        <Bullets
          items={[
            "Loss of research data",
            "Experimental outcomes or inaccuracies",
            "Any indirect or consequential damages",
          ]}
        />
        <p>Use the platform at your own discretion.</p>
      </Section>

      <Section n="11" title="Termination">
        <p>We reserve the right to:</p>
        <Bullets
          items={[
            "Suspend or terminate accounts violating terms",
            "Remove content deemed inappropriate",
          ]}
        />
        <p>Users may terminate their account at any time.</p>
      </Section>

      <Section n="12" title="Changes to Terms">
        <p>We may update these Terms periodically. Continued use implies acceptance of updated terms.</p>
      </Section>

      <Section n="13" title="Contact">
        <p>
          For any queries/suggestions contact:{" "}
          <a
            href="mailto:hello@resynote.com"
            className="text-blue-600 hover:underline"
            data-testid="terms-contact-email"
          >
            hello@resynote.com
          </a>
        </p>
      </Section>
    </LegalPage>
  );
}
